interface ProcessSectionProps {
  isVisible: boolean;
  sectionRef: React.RefObject<HTMLElement | null>;
  name?: string;
  description?: string;
  background?: string;
  nameColor?: string;
  descriptionColor?: string;
  items?: any[]; // Nhận items từ props thay vì tự call API
}

interface ProcessItem {
  id: string;
  step?: string;
  icon: string;
  title: string;
  description: string;
  keyword?: string;
  circleColor?: string;
  iconBg?: string;
}

export default function ProcessSection({
  isVisible,
  sectionRef,
  name = 'Quy Trình Làm Việc',
  description = 'Phương pháp làm việc chuyên nghiệp, minh bạch từng bước',
  background,
  nameColor,
  descriptionColor,
  items = [] // Nhận từ props
}: ProcessSectionProps) {
  const processes: ProcessItem[] = items || [];
  const loading = false; // Không cần loading vì đã có data từ props

  const backgroundStyle = background
    ? {
        backgroundImage: background.startsWith('http') || background.startsWith('/')
          ? `url(${background})`
          : undefined,
        backgroundColor: background.startsWith('http') || background.startsWith('/') ? undefined : background,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
      }
    : undefined;

  return (
    <section 
      ref={sectionRef}
      data-section="process"
      className={`py-12 md:py-16 relative overflow-hidden ${!background ? 'bg-gradient-to-br from-[#2a4b7c] to-[#1e3a5f]' : ''}`}
      style={backgroundStyle}
    >
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Header - Title and Description */}
        {(name || description) && (
          <div className={`text-center mb-8 md:mb-12 transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`}>
            {name && (
              <h2 
                className="text-2xl md:text-3xl lg:text-4xl font-bold mb-4 leading-tight tracking-tight max-w-4xl mx-auto"
                style={{ color: nameColor || 'white' }}
              >
                {name}
              </h2>
            )}
            {description && (
              <p 
                className="text-base md:text-lg max-w-3xl mx-auto leading-relaxed"
                style={{ color: descriptionColor || '#d1d5db' }}
              >
                {description}
              </p>
            )}
          </div>
        )}

        {/* Mobile: Card Layout */}
        {loading ? (
          <div className="md:hidden text-center py-12">
            <div className="inline-block w-16 h-16 border-4 border-white border-t-transparent rounded-full animate-spin"></div>
            <p className="mt-4 text-white">Đang tải...</p>
          </div>
        ) : processes.length === 0 ? (
          <div className="md:hidden text-center py-12">
            <p className="text-white">Chưa có nội dung</p>
          </div>
        ) : (
          <div className="md:hidden space-y-4">
            {processes.map((process, index) => (
              <div
                key={process.id}
              className={`bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20 transition-all duration-1000 ${
                isVisible
                  ? 'opacity-100 translate-y-0'
                  : 'opacity-0 translate-y-8'
              }`}
              style={{ transitionDelay: `${index * 150}ms` }}
            >
              <div className="flex items-start gap-4">
                {/* Icon Circle */}
                <div
                  className={`flex-shrink-0 w-14 h-14 ${process.iconBg || 'bg-cyan-500'} rounded-full flex items-center justify-center shadow-lg`}
                >
                  <i className={`${process.icon} text-white text-xl`}></i>
                </div>
                
                {/* Content */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-xs font-bold text-[#06b6d4] bg-[#06b6d4]/20 px-2 py-1 rounded">
                      {process.step}
                    </span>
                    <h3 className="text-base font-bold text-white">
                      {process.title}
                    </h3>
                  </div>
                  <p className="text-sm text-gray-300 leading-relaxed">
                    {process.description}
                  </p>
                </div>
              </div>
            </div>
          ))}
          </div>
        )}

        {/* Desktop: Vertical Timeline Layout */}
        {loading ? (
          <div className="hidden md:block text-center py-12">
            <div className="inline-block w-16 h-16 border-4 border-white border-t-transparent rounded-full animate-spin"></div>
            <p className="mt-4 text-white">Đang tải...</p>
          </div>
        ) : processes.length === 0 ? (
          <div className="hidden md:block text-center py-12">
            <p className="text-white">Chưa có nội dung</p>
          </div>
        ) : (
          <div className="hidden md:block relative pb-8">
          {/* Vertical center line */}
          <div className="absolute left-1/2 transform -translate-x-1/2 top-0 bottom-0 w-0.5 bg-[#06b6d4]/30"></div>

          <div className="space-y-8">
            {processes.map((process, index) => {
              // First item (index 0) on right, then alternate
              const align = index % 2 === 0 ? 'right' : 'left';
              
              return (
                <div
                  key={process.id}
                  className={`relative grid md:grid-cols-2 gap-8 items-center min-h-[120px] transition-all duration-1000 ${
                    isVisible
                      ? 'opacity-100 translate-x-0'
                      : align === 'left'
                      ? 'opacity-0 -translate-x-32'
                      : 'opacity-0 translate-x-32'
                  }`}
                  style={{ transitionDelay: `${index * 200}ms` }}
                >
                  {align === 'left' ? (
                    <>
                      <div className="md:text-right md:pr-16">
                        <h3 className="text-lg font-bold text-white mb-2">
                          {process.title}
                        </h3>
                        <p className="text-base text-gray-300 leading-relaxed">
                          {process.description}
                        </p>
                      </div>
                      <div className="hidden md:block"></div>
                    </>
                  ) : (
                    <>
                      <div className="hidden md:block"></div>
                      <div className="md:pl-16">
                        <h3 className="text-lg font-bold text-white mb-2">
                          {process.title}
                        </h3>
                        <p className="text-base text-gray-300 leading-relaxed">
                          {process.description}
                        </p>
                      </div>
                    </>
                  )}
                  {/* Icon Circle - Positioned on timeline */}
                  <div
                    className={`absolute left-1/2 top-1/2 transform -translate-x-1/2 -translate-y-1/2 w-16 h-16 ${process.iconBg || 'bg-[#06b6d4]'} rounded-full flex items-center justify-center shadow-lg z-10 transition-all duration-1000 ${
                      isVisible ? 'scale-100 opacity-100' : 'scale-0 opacity-0'
                    }`}
                    style={{ transitionDelay: `${index * 200}ms` }}
                  >
                    <i className={`${process.icon} text-white text-2xl`}></i>
                  </div>
                </div>
              );
            })}
          </div>
          </div>
        )}
      </div>
    </section>
  );
}

